import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ColumnDef, type RowSelectionState } from '@tanstack/react-table';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
  CheckCircle2,
  MoreHorizontal,
  Plus,
  Receipt,
  XCircle,
} from 'lucide-react';
import type { PaymentStatus } from '@runik/shared';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { Button } from '../../../../components/ui/button';
import { Checkbox } from '../../../../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../components/ui/alert-dialog';
import { DataTable } from '../../../../components/data/DataTable';
import { EmptyState } from '../../../../components/data/EmptyState';
import { PaymentFormDialog } from '../../../../components/payments/PaymentFormDialog';
import { PaymentStatusBadge } from '../../../../components/payments/PaymentStatusBadge';
import { toast } from '../../../../components/ui/sonner';
import { ApiError } from '../../../../lib/auth/client';
import {
  bulkPaymentAction,
  listPayments,
  paymentsKey,
  updatePayment,
  type PaymentListRow,
} from '../../../../lib/queries/payments';

const STATUS_OPTIONS: { label: string; value: PaymentStatus }[] = [
  { label: 'Beklemede', value: 'pending' },
  { label: 'Ödendi', value: 'paid' },
  { label: 'Vadesi Geçmiş', value: 'overdue' },
  { label: 'İptal', value: 'cancelled' },
];

function PaymentsPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<PaymentStatus | 'all'>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [selection, setSelection] = useState<RowSelectionState>({});
  const [bulkAction, setBulkAction] = useState<'mark_paid' | 'cancel' | null>(null);

  const query = {
    page,
    limit: 25,
    status: statusFilter !== 'all' ? [statusFilter] : undefined,
  };
  const payments = useQuery({
    queryKey: paymentsKey(query),
    queryFn: () => listPayments(query),
  });

  const transitionMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: PaymentStatus }) =>
      updatePayment(id, { status }),
    onSuccess: () => {
      toast.success('Durum güncellendi');
      qc.invalidateQueries({ queryKey: ['payments'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.problem.detail ?? err.problem.title : 'Hata oluştu');
    },
  });

  const bulkMutation = useMutation({
    mutationFn: (action: 'mark_paid' | 'cancel') =>
      bulkPaymentAction({ ids: Object.keys(selection), action }),
    onSuccess: (resp) => {
      toast.success(
        `${resp.updated.length} ödeme güncellendi${resp.skipped.length ? `, ${resp.skipped.length} atlandı` : ''}`,
      );
      setSelection({});
      qc.invalidateQueries({ queryKey: ['payments'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.problem.detail ?? err.problem.title : 'Toplu işlem başarısız');
    },
  });

  const selectedCount = Object.keys(selection).length;

  const columns = useMemo<ColumnDef<PaymentListRow, unknown>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllRowsSelected() ||
              (table.getIsSomeRowsSelected() ? 'indeterminate' : false)
            }
            onCheckedChange={(v) => table.toggleAllRowsSelected(!!v)}
            aria-label="Tümünü seç"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(v) => row.toggleSelected(!!v)}
            aria-label="Satırı seç"
          />
        ),
      },
      { accessorKey: 'invoiceNo', header: 'Fatura' },
      { accessorKey: 'customerName', header: 'Müşteri' },
      {
        accessorKey: 'amount',
        header: 'Tutar',
        cell: ({ row }) => `${row.original.amount} ${row.original.currency}`,
      },
      {
        accessorKey: 'status',
        header: 'Durum',
        cell: ({ row }) => <PaymentStatusBadge status={row.original.status} />,
      },
      {
        accessorKey: 'dueDate',
        header: 'Vade',
        cell: ({ row }) => format(new Date(row.original.dueDate), 'd MMM yyyy', { locale: tr }),
      },
      {
        accessorKey: 'paidAt',
        header: 'Ödendiği Tarih',
        cell: ({ row }) =>
          row.original.paidAt
            ? format(new Date(row.original.paidAt), 'd MMM yyyy', { locale: tr })
            : '—',
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const p = row.original;
          const transitions: PaymentStatus[] =
            p.status === 'pending'
              ? ['paid', 'cancelled', 'overdue']
              : p.status === 'overdue'
                ? ['paid', 'cancelled']
                : [];
          if (transitions.length === 0) {
            return <span className="text-slate-300">—</span>;
          }
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {transitions.map((s) => (
                  <DropdownMenuItem
                    key={s}
                    onSelect={() => transitionMutation.mutate({ id: p.id, status: s })}
                  >
                    {s === 'paid' ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    {s === 'paid' ? 'Ödendi' : s === 'cancelled' ? 'İptal Et' : 'Vadesi Geçmiş'}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [transitionMutation],
  );

  return (
    <>
      <PageHeader
        title="Ödemeler"
        description="Müşteri ödemeleri ve durumları"
        actions={
          <div className="flex items-center gap-2">
            {selectedCount > 0 && (
              <>
                <span className="text-sm text-slate-500">{selectedCount} seçili</span>
                <Button variant="outline" size="sm" onClick={() => setBulkAction('mark_paid')}>
                  Ödendi olarak işaretle
                </Button>
                <Button variant="outline" size="sm" onClick={() => setBulkAction('cancel')}>
                  İptal et
                </Button>
              </>
            )}
            <Button onClick={() => setFormOpen(true)}>
              <Plus className="h-4 w-4" /> Yeni Ödeme
            </Button>
          </div>
        }
      />
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v as PaymentStatus | 'all');
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm durumlar</SelectItem>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DataTable
          columns={columns}
          data={payments.data?.data}
          loading={payments.isLoading}
          getRowId={(row) => row.id}
          rowSelection={selection}
          onRowSelectionChange={setSelection}
          empty={
            <EmptyState
              icon={Receipt}
              title="Henüz ödeme yok"
              description="İlk ödeme kaydını oluşturun."
              action={
                <Button onClick={() => setFormOpen(true)}>
                  <Plus className="h-4 w-4" /> Ödeme Ekle
                </Button>
              }
            />
          }
          pagination={
            payments.data
              ? {
                  page: payments.data.page,
                  limit: payments.data.limit,
                  total: payments.data.total,
                  onPageChange: setPage,
                }
              : undefined
          }
        />
      </div>

      <PaymentFormDialog open={formOpen} onOpenChange={setFormOpen} />

      <AlertDialog open={!!bulkAction} onOpenChange={(v) => !v && setBulkAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {bulkAction === 'mark_paid'
                ? `${selectedCount} ödeme ödendi olarak işaretlensin mi?`
                : `${selectedCount} ödeme iptal edilsin mi?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Geçersiz durum geçişlerine sahip ödemeler atlanacak.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (bulkAction) bulkMutation.mutate(bulkAction);
                setBulkAction(null);
              }}
            >
              Onayla
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export const Route = createFileRoute('/_app/admin/payments/')({ component: PaymentsPage });
